import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';
import { Flame, Trophy, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import { Card } from './ui/card';

const StreakCard = ({ refreshKey }) => {
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const data = await api.getStreak();
        setStreak(data);
      } catch (error) {
        console.error('Error fetching streak:', error);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [refreshKey]);

  if (loading) {
    return (
      <Card className="p-6 rounded-3xl border-2 border-slate-200 bg-white" data-testid="streak-card-loading">
        <div className="animate-pulse h-24" />
      </Card>
    );
  }

  const current = streak?.current_streak || 0;
  const longest = streak?.longest_streak || 0;
  const practicedToday = streak?.practiced_today;
  const isActive = current > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ scale: 1.02, y: -4 }}
    >
      <Card
        className={`p-6 rounded-3xl border-2 relative overflow-hidden ${
          isActive
            ? 'border-orange-300 bg-gradient-to-br from-orange-50 via-amber-50 to-white'
            : 'border-slate-200 bg-white'
        }`}
        data-testid="streak-card"
      >
        {/* Decorative flame background */}
        {isActive && practicedToday && (
          <motion.div
            className="absolute top-2 right-2 opacity-10"
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            <Flame className="w-32 h-32 text-orange-400 fill-orange-300" />
          </motion.div>
        )}

        <div className="relative">
          <div className="flex items-start justify-between mb-4">
            <div className={`p-3 rounded-2xl ${isActive ? 'bg-orange-500' : 'bg-slate-200'}`}>
              <Flame
                className={`w-6 h-6 ${isActive ? 'text-white fill-white' : 'text-slate-400'}`}
                strokeWidth={2.5}
              />
            </div>
          </div>

          <div className="mb-3">
            <div className="flex items-baseline gap-2">
              <span
                className={`text-5xl font-heading font-bold ${
                  isActive ? 'text-orange-600' : 'text-slate-400'
                }`}
                data-testid="streak-current"
              >
                {current}
              </span>
              <span className="text-lg text-slate-600 font-heading font-bold">
                day{current === 1 ? '' : 's'}
              </span>
            </div>
            <p className="text-sm text-slate-600 mt-1 font-semibold" data-testid="streak-status">
              {practicedToday ? (
                <>🔥 On fire! Come back tomorrow to keep it going.</>
              ) : isActive ? (
                <>Practice today to keep your streak alive!</>
              ) : current === 0 && longest === 0 ? (
                <>Start practicing to build your streak!</>
              ) : (
                <>Your streak broke. Start a new one today!</>
              )}
            </p>
          </div>

          <div className="flex items-center gap-4 pt-3 border-t border-slate-200/70">
            <div className="flex items-center gap-2">
              <Trophy className="w-4 h-4 text-amber-500" strokeWidth={2.5} />
              <div>
                <p className="text-xs text-slate-500 font-semibold">Longest</p>
                <p className="text-sm font-heading font-bold text-slate-800" data-testid="streak-longest">
                  {longest} day{longest === 1 ? '' : 's'}
                </p>
              </div>
            </div>
            {streak?.last_practice_date && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-emerald-500" strokeWidth={2.5} />
                <div>
                  <p className="text-xs text-slate-500 font-semibold">Last</p>
                  <p className="text-sm font-heading font-bold text-slate-800" data-testid="streak-last-date">
                    {practicedToday ? 'Today' : new Date(streak.last_practice_date).toLocaleDateString('en-SG', { day: 'numeric', month: 'short' })}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default StreakCard;
